/**
 * AppShell — Unified navigation wrapper for the merged app.
 *
 * Desktop: sticky top nav with tabs (Home, Feed, Sources, Tracking, Settings)
 * Mobile: sticky bottom tab bar with the same tabs
 */

import { type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Sparkles, Layers, Newspaper, Library, Radar, Settings } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const FEED_NAV = [
  { path: '/feed', label: 'Feed', icon: Newspaper },
  { path: '/sources', label: 'Sources', icon: Library },
  { path: '/tracking', label: 'Tracking', icon: Radar },
] as const;

const WORKSPACE_NAV = [
  { path: '/', label: 'Workspace', icon: Layers },
  { path: '/settings', label: 'Settings', icon: Settings },
] as const;

const NAV_ITEMS = [...FEED_NAV, ...WORKSPACE_NAV];

interface AppShellProps {
  children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { pathname } = useLocation();
  const { signOut } = useAuth();

  const activePath = NAV_ITEMS.find((item) => item.path === pathname)?.path ?? '/';

  return (
    <div
      className="min-h-screen pb-[calc(3.5rem+env(safe-area-inset-bottom))] md:pb-0"
      style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
    >
      {/* Desktop top bar */}
      <header className="sticky top-0 z-40 glass-header hidden md:block">
        <div className="max-w-7xl mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-brand-600 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <span className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              Personal Insights
            </span>
          </Link>

          <nav className="flex items-center gap-1">
            {FEED_NAV.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="px-3 py-1.5 type-label rounded-md"
                style={{
                  color: activePath === item.path ? 'var(--text-primary)' : 'var(--text-tertiary)',
                  background: activePath === item.path ? 'var(--bg-tertiary)' : 'transparent',
                  transition: 'background 80ms ease-in, color 80ms ease-in',
                }}
              >
                {item.label}
              </Link>
            ))}

            <div className="w-px h-4 mx-1" style={{ background: 'var(--border-primary)' }} />

            {WORKSPACE_NAV.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="px-3 py-1.5 type-label rounded-md"
                style={{
                  color: activePath === item.path ? 'var(--text-primary)' : 'var(--text-tertiary)',
                  background: activePath === item.path ? 'var(--bg-tertiary)' : 'transparent',
                  transition: 'background 80ms ease-in, color 80ms ease-in',
                }}
              >
                {item.label}
              </Link>
            ))}

            <div className="w-px h-4 mx-2" style={{ background: 'var(--border-primary)' }} />

            <button
              onClick={() => signOut()}
              className="px-2 py-1.5 type-label rounded-md"
              style={{ color: 'var(--text-tertiary)', transition: 'background 80ms ease-in' }}
            >
              Sign out
            </button>
          </nav>
        </div>
      </header>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-40 glass-header md:hidden">
        <div className="px-4 h-11 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-brand-600 flex items-center justify-center">
              <Sparkles className="h-3 w-3 text-white" />
            </div>
            <span className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
              Personal Insights
            </span>
          </Link>
          <button
            onClick={() => signOut()}
            className="px-2 py-1 type-label rounded-md"
            style={{ color: 'var(--text-tertiary)' }}
          >
            Sign out
          </button>
        </div>
      </header>

      {/* Page content */}
      {children}

      {/* Mobile bottom tab bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 glass-header md:hidden pb-[env(safe-area-inset-bottom)]"
        style={{ borderTop: '1px solid var(--border-primary)' }}
      >
        <div className="flex items-center justify-around h-[3.25rem]">
          {FEED_NAV.map((item) => {
            const Icon = item.icon;
            const isActive = activePath === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className="flex flex-col items-center gap-0.5 px-4 py-1 rounded-lg"
                style={{
                  color: isActive ? 'var(--accent-primary)' : 'var(--text-tertiary)',
                  transition: 'color 80ms ease-in',
                }}
              >
                <Icon className="h-4 w-4" />
                <span className="text-[11px] font-medium">{item.label}</span>
              </Link>
            );
          })}

          <div className="w-px h-4 mx-1" style={{ background: 'var(--border-primary)' }} />

          {WORKSPACE_NAV.map((item) => {
            const Icon = item.icon;
            const isActive = activePath === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className="flex flex-col items-center gap-0.5 px-4 py-1 rounded-lg"
                style={{
                  color: isActive ? 'var(--accent-primary)' : 'var(--text-tertiary)',
                  transition: 'color 80ms ease-in',
                }}
              >
                <Icon className="h-4 w-4" />
                <span className="text-[11px] font-medium">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
